<template>
    <div class="search-sort-actions">
        <div ref="sortDropdownRoot" class="search-sort-dropdown">
            <div
                class="search-sort-trigger"
                :class="{ 'is-open': sortDropdownOpen, 'is-disabled': isLoading }"
                role="button"
                tabindex="0"
                :aria-expanded="sortDropdownOpen"
                :aria-disabled="isLoading ? 'true' : 'false'"
                aria-haspopup="listbox"
                :aria-controls="listboxId"
                @click.stop="toggleSortDropdown"
                @keydown.enter.prevent="toggleSortDropdown"
                @keydown.space.prevent="toggleSortDropdown"
            >
                <span class="search-sort-trigger-text">
                    <span class="search-sort-trigger-prefix">Sort by:</span>
                    <span class="search-sort-trigger-value">{{ sortTriggerLabel }}</span>
                </span>
            </div>
            <div
                v-show="sortDropdownOpen"
                :id="listboxId"
                class="search-sort-menu"
                role="listbox"
                aria-label="Sort search results"
            >
                <button
                    v-for="opt in sortSelectOptions"
                    :key="opt.value"
                    type="button"
                    role="option"
                    class="search-sort-menu-item"
                    :class="{ 'is-active': sortBy === opt.value }"
                    :aria-selected="sortBy === opt.value"
                    @click="selectSortOption(opt.value)"
                >
                    {{ opt.label }}
                </button>
            </div>
        </div>
        <e-icon
            :icon="sortDir === 'desc' ? 'sort-amount-down' : 'sort-amount-up'"
            class="search-sort-direction-icon"
            :class="{ 'is-disabled': isRelevanceSort || isLoading }"
            role="button"
            :tabindex="isRelevanceSort || isLoading ? -1 : 0"
            :aria-label="sortDirectionAriaLabel"
            @click.stop="onToggleDirection"
            @keydown.enter.prevent="onToggleDirection"
            @keydown.space.prevent="onToggleDirection"
        />
    </div>
</template>

<script>
import {
    buildSearchSortSelectOptions,
    getSortDirectionAriaLabel,
    SEARCH_SORT_RELEVANCE,
} from '@/search/searchSortOptions.js';

export default {
    name: 'SearchSortToolbar',

    props: {
        sortBy: {
            type: String,
            required: true,
        },
        sortDir: {
            type: String,
            required: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        /** Stable id for listbox (must be unique per page, e.g. global-search-sort-listbox). */
        listboxId: {
            type: String,
            required: true,
        },
    },

    emits: ['select-sort', 'toggle-direction'],

    data() {
        return {
            sortDropdownOpen: false,
            sortDropdownAttachTimeoutId: null,
        };
    },

    computed: {
        isRelevanceSort() {
            return this.sortBy === SEARCH_SORT_RELEVANCE;
        },

        sortSelectOptions() {
            return buildSearchSortSelectOptions();
        },

        sortTriggerLabel() {
            const opt = this.sortSelectOptions.find(o => o.value === this.sortBy);
            if (opt) {
                return opt.label;
            }
            return 'Relevance';
        },

        sortDirectionAriaLabel() {
            return getSortDirectionAriaLabel(this.sortBy, this.sortDir);
        },
    },

    beforeUnmount() {
        this.detachSortDropdownOutsideListener();
    },

    methods: {
        /** Allow parents to close the menu when resetting UI (e.g. modal clear). */
        closeDropdown() {
            this.closeSortDropdownOnly();
        },

        detachSortDropdownOutsideListener() {
            if (this.sortDropdownAttachTimeoutId != null) {
                clearTimeout(this.sortDropdownAttachTimeoutId);
                this.sortDropdownAttachTimeoutId = null;
            }
            document.removeEventListener('click', this._sortDropdownOutsideHandler, true);
        },

        _sortDropdownOutsideHandler(e) {
            const root = this.$refs.sortDropdownRoot;
            if (root && !root.contains(e.target)) {
                this.sortDropdownOpen = false;
                this.detachSortDropdownOutsideListener();
            }
        },

        toggleSortDropdown() {
            if (this.isLoading) {
                return;
            }
            if (this.sortDropdownOpen) {
                this.sortDropdownOpen = false;
                this.detachSortDropdownOutsideListener();

                return;
            }
            this.sortDropdownOpen = true;
            this.$nextTick(() => {
                this.sortDropdownAttachTimeoutId = setTimeout(() => {
                    this.sortDropdownAttachTimeoutId = null;
                    document.addEventListener('click', this._sortDropdownOutsideHandler, true);
                }, 0);
            });
        },

        closeSortDropdownOnly() {
            if (!this.sortDropdownOpen) {
                return;
            }
            this.sortDropdownOpen = false;
            this.detachSortDropdownOutsideListener();
        },

        selectSortOption(value) {
            if (this.sortBy === value) {
                this.closeSortDropdownOnly();

                return;
            }
            this.$emit('select-sort', value);
            this.closeSortDropdownOnly();
        },

        onToggleDirection() {
            if (this.isRelevanceSort || this.isLoading) {
                return;
            }
            this.$emit('toggle-direction');
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~assets/scss/toolbox.scss';
@import '../app/GlobalSearch/_global-search-variables.scss';

.search-sort-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    justify-content: flex-end;
}

.search-sort-dropdown {
    position: relative;
    flex: 0 1 auto;
    min-width: 0;
    max-width: min(100%, 20rem);
}

.search-sort-direction-icon {
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 24px !important;
    height: 24px !important;
    min-width: 24px;
    min-height: 24px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--text-color, #{$text-color});
    cursor: pointer;
    border-radius: $radius-small;
    font-size: 24px !important;
    line-height: 1 !important;
    transition:
        color 0.15s ease,
        opacity 0.15s ease;

    &:hover:not(.is-disabled) {
        color: var(--primary-color, #{$primary});
    }

    &:focus-visible {
        outline: 2px solid var(--primary-color, #{$primary});
        outline-offset: 2px;
    }

    &.is-disabled {
        opacity: 0.45;
        cursor: not-allowed;
        pointer-events: none;
    }
}

.search-sort-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-4;
    width: 100%;
    min-height: 2.25rem;
    padding: $space-3 $space-4;
    padding-right: 0;
    border-radius: $radius;
    background: var(--surface-card, #{$white});
    color: var(--text-color, #{$text-color});
    font-size: $app-font-size;
    font-weight: $weight-medium;
    line-height: 1.25;
    cursor: pointer;
    text-align: left;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;

    &:hover:not(.is-disabled) {
        border-color: var(--surface-light-border, #{$surface-light-border});
    }

    &:focus-visible {
        outline: 2px solid var(--primary-color, #{$primary});
        outline-offset: 1px;
    }

    &.is-disabled {
        opacity: 0.55;
        cursor: not-allowed;
        pointer-events: none;
    }
}

.search-sort-trigger-text {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.35em;
    min-width: 0;
    font-weight: 700;
    font-size: 14px;
}

.search-sort-menu {
    position: absolute;
    right: -30px;
    top: 100%;
    z-index: 20;
    min-width: min(12.5rem, calc(100vw - 3rem));
    max-width: min(20rem, calc(100vw - 2rem));
    margin-top: 2px;
    border: 1px solid var(--surface-border, #{$surface-border});
    border-radius: $radius;
    background: var(--surface-card, #{$white});
    box-shadow:
        0 10px 15px -3px rgba(15, 23, 42, 0.08),
        0 4px 6px -4px rgba(15, 23, 42, 0.06);
}

.search-sort-menu-item {
    display: block;
    width: 100%;
    border: none;
    padding: 8px 16px;
    background: transparent;
    color: var(--text-strong, #{$text-color});
    font-size: $app-font-size;
    font-weight: $weight-normal;
    line-height: 1.35;
    text-align: left;
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover:not(.is-active) {
        background: var(--surface-50, #{$surface-50});
    }

    &:focus-visible {
        outline: none;
        background: var(--surface-100, #{$surface-100});
    }

    &.is-active {
        color: var(--primary-color, #{$primary});
        font-weight: 700;
    }
}
</style>
